import {getAuthModel} from '../../redux/auth'

const API3_URL: string | undefined = process.env.REACT_APP_API3_URL

export interface PresignedUrlResponse {
  url: string
  key: string
  thumbUrl: string
  thumbKey: string
}

export interface S3UploadResult {
  key: string
  thumbKey: string
  fileName: string
  fileType: string
  fileSize: number
  width: number
  height: number
  duration: number
}

/**
 * Request presigned URLs for multiple files (source + thumbnail) in one batch.
 */
export const getPresignedUrls = async (
  files: {file_name: string; file_type: string; file_size: number}[],
  sourceGroupId: string
): Promise<PresignedUrlResponse[]> => {
  const auth = getAuthModel()
  const response = await fetch(`${API3_URL}/source/presigned-urls`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth?.access_token}`,
    },
    body: JSON.stringify({
      uploads: files,
      source_group_id: sourceGroupId,
    }),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to get presigned URLs')
  }
  return response.json()
}

/**
 * Upload a file directly to S3 using a presigned URL.
 */
export const uploadFileToS3 = async (
  file: File | Blob,
  presignedUrl: string,
  contentType: string,
  onProgress?: (percent: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', presignedUrl)
    xhr.setRequestHeader('Content-Type', contentType)
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`S3 upload failed with status ${xhr.status}`))
    xhr.onerror = () => reject(new Error('S3 upload network error'))
    xhr.send(file)
  })
}

/**
 * Generate a 180px-wide PNG thumbnail blob from an image or video file.
 * Returns null for unsupported types (audio, pdf, etc).
 */
export const generateThumbnailBlob = (
  file: File,
  thumbWidth = 480
): Promise<{blob: Blob; width: number; height: number; duration: number} | null> => {
  const type = file.type.split('/')[0]

  if (type === 'image') {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        const scale = thumbWidth / img.width
        const thumbHeight = Math.round(img.height * scale)
        const canvas = document.createElement('canvas')
        canvas.width = thumbWidth
        canvas.height = thumbHeight
        const ctx = canvas.getContext('2d')
        ctx?.drawImage(img, 0, 0, thumbWidth, thumbHeight)
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(img.src)
            resolve(blob ? {blob, width: img.width, height: img.height, duration: -1} : null)
          },
          'image/jpeg',
          0.85
        )
      }
      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        resolve(null)
      }
      img.src = URL.createObjectURL(file)
    })
  }

  if (type === 'video') {
    return new Promise((resolve) => {
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.muted = true
      video.playsInline = true

      video.onloadeddata = () => {
        video.currentTime = Math.min(3, video.duration * 0.25)
      }

      video.onseeked = () => {
        const scale = thumbWidth / video.videoWidth
        const thumbHeight = Math.round(video.videoHeight * scale)
        const canvas = document.createElement('canvas')
        canvas.width = thumbWidth
        canvas.height = thumbHeight
        const ctx = canvas.getContext('2d')
        ctx?.drawImage(video, 0, 0, thumbWidth, thumbHeight)
        canvas.toBlob(
          (blob) => {
            const result = blob
              ? {
                  blob,
                  width: video.videoWidth,
                  height: video.videoHeight,
                  duration: video.duration,
                }
              : null
            URL.revokeObjectURL(video.src)
            resolve(result)
          },
          'image/jpeg',
          0.85
        )
      }

      video.onerror = () => {
        URL.revokeObjectURL(video.src)
        resolve(null)
      }

      video.src = URL.createObjectURL(file)
      video.load()
    })
  }

  // Audio, PDF, etc — no thumbnail
  return Promise.resolve(null)
}

/**
 * Confirm a batch of S3 uploads with the backend.
 */
export const confirmBatchS3Upload = async (
  sourceGroupId: string,
  uploads: S3UploadResult[]
): Promise<any> => {
  const auth = getAuthModel()
  const response = await fetch(`${API3_URL}/source/confirm-batch-upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${auth?.access_token}`,
    },
    body: JSON.stringify({
      source_group_id: sourceGroupId,
      uploads,
    }),
  })
  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(error.message || 'Failed to confirm batch upload')
  }
  return response.json()
}
